import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ModuleKey, PermissionLevel, PermissionMatrix } from '@tww/shared';
import { MODULES, MODULE_NAMES, ROLE_KEYS, ROLE_NAMES, type RoleKey } from '@tww/shared';
import { api } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { ErrorState, Skeleton } from '../../ui/basics';
import { useToast } from '../../ui/Toast';
import { useUnsaved } from './adminUtil';

const LEVELS: { value: PermissionLevel; label: string }[] = [
  { value: 'none', label: 'kein Zugriff' },
  { value: 'read', label: 'lesen' },
  { value: 'write', label: 'bearbeiten' },
];

/** Kurzbeschreibung einer Rolle aus der aktuellen Matrix, z. B. „Bearbeiten: Aktionen, Flächen · Lesen: Kontakte“ */
function describe(row: Record<ModuleKey, PermissionLevel>): { short: string; full: string } {
  const by = (l: PermissionLevel) => MODULES.filter((m) => row[m] === l).map((m) => MODULE_NAMES[m]);
  const write = by('write');
  const read = by('read');
  if (write.length === MODULES.length) return { short: 'Bearbeiten: alle Module', full: 'Bearbeiten: alle Module' };
  if (!write.length && read.length === MODULES.length) return { short: 'Lesen: alle Module', full: 'Lesen: alle Module' };
  const cut = (l: string[]) => (l.length > 3 ? `${l.slice(0, 3).join(', ')} +${l.length - 3}` : l.join(', '));
  const parts = (f: (l: string[]) => string) => [write.length ? `Bearbeiten: ${f(write)}` : '', read.length ? `Lesen: ${f(read)}` : ''].filter(Boolean).join(' · ') || 'kein Zugriff';
  return { short: parts(cut), full: parts((l) => l.join(', ')) };
}

/** Rollen und Rechte: links die Rolle wählen, rechts je Modul festlegen, was sie darf. */
export function RolesPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const { refresh } = useAuth();
  const q = useQuery({ queryKey: ['permissions'], queryFn: () => api.get<{ matrix: PermissionMatrix }>('/permissions').then((r) => r.matrix) });
  const [m, setM] = useState<PermissionMatrix | null>(null);
  const [role, setRole] = useState<RoleKey>('forst');
  useEffect(() => {
    if (q.data) setM(structuredClone(q.data));
  }, [q.data]);
  const dirty = Boolean(m && q.data && JSON.stringify(m) !== JSON.stringify(q.data));
  useUnsaved(dirty);
  const save = useMutation({
    mutationFn: () => api.put('/permissions', { matrix: m }),
    onSuccess: async () => {
      qc.invalidateQueries({ queryKey: ['permissions'] });
      await refresh();
      toast('Rechte gespeichert');
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  if (q.isLoading || !m) return <Skeleton />;
  if (q.isError) return <ErrorState error={q.error} retry={() => q.refetch()} />;
  const dirtyRoles = ROLE_KEYS.filter((r) => JSON.stringify(m[r]) !== JSON.stringify(q.data![r]));
  const locked = (mod: ModuleKey) => role === 'admin' && mod === 'users';
  const setLevel = (mod: ModuleKey, level: PermissionLevel) => setM({ ...m, [role]: { ...m[role], [mod]: level } });
  const setAll = (level: PermissionLevel) => setM({ ...m, [role]: Object.fromEntries(MODULES.map((k) => [k, locked(k) ? m[role][k] : level])) as Record<ModuleKey, PermissionLevel> });
  return (
    <div className="grid g-side">
      <div className="panel">
        <div className="ph">
          <h2>Rollen</h2>
        </div>
        <div className="pb side-list side-list-pad">
          {ROLE_KEYS.map((r) => {
            const d = describe(m[r]);
            return (
              <button key={r} type="button" className={r === role ? 'on' : ''} onClick={() => setRole(r)} title={d.full}>
                <span>
                  {ROLE_NAMES[r]}
                  <span className="d">{d.short}</span>
                </span>
                {dirtyRoles.includes(r) && <span className="n">geändert</span>}
              </button>
            );
          })}
        </div>
        <div className="pb mut small pb-top">Jeder Nutzer hat genau eine Rolle. Rechte gelten je Modul, nicht je Feld.</div>
      </div>
      <div className="panel">
        <div className="ph">
          <h2>{ROLE_NAMES[role]}</h2>
          <span className="btn-row">
            <button type="button" className="btn sm" onClick={() => setAll('read')}>
              alles lesen
            </button>
            <button type="button" className="btn sm" onClick={() => setAll('write')}>
              alles bearbeiten
            </button>
            <button type="button" className="btn sm primary" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
              Speichern{dirtyRoles.length > 1 ? ` (${dirtyRoles.length} Rollen)` : ''}
            </button>
          </span>
        </div>
        <div className="tw">
          <table>
            <thead>
              <tr>
                <th className="col-40">Modul</th>
                <th>Zugriff</th>
              </tr>
            </thead>
            <tbody>
              {MODULES.map((mod) => (
                <tr key={mod} title={locked(mod) ? 'Admin braucht immer Zugriff auf Nutzer' : undefined}>
                  <td>{MODULE_NAMES[mod]}</td>
                  <td>
                    <div className="seg" role="radiogroup" aria-label={`${MODULE_NAMES[mod]} für ${ROLE_NAMES[role]}`}>
                      {LEVELS.map((l) => (
                        <button key={l.value} type="button" role="radio" aria-checked={m[role][mod] === l.value} className={m[role][mod] === l.value ? `on ${l.value}` : ''} onClick={() => setLevel(mod, l.value)} disabled={locked(mod)}>
                          {l.label}
                        </button>
                      ))}
                    </div>
                    {locked(mod) && <span className="sm">Admin braucht immer Zugriff auf Nutzer</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
